import { useCallback, useEffect, useMemo, useState } from 'react'
import Add from '@mui/icons-material/Add'
import { Box, Button, Paper, Typography } from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import useProjectFieldInfos from '@/hooks/useProjectFieldInfos'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import {
  useGetProjectGridFieldRules,
  useUpdateProjectGridFieldRule,
} from '@/service-library/hooks/project-grid-field-rules'
import queryKeys from '@/service-library/query-keys'
import { ProjectGridField } from '@/types/fields'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import FieldRules from '@/components/project-settings/field-rules/FieldRules'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import RuleSettings, { FormValues } from '@/components/rules/RuleSettings'
import SettingsPage from '@/components/shared/settings/SettingsPage'
import FieldRulesFieldList from './FieldRulesFieldList'

export default function FieldRulesPage() {
  const { updateProjectGridFieldRuleAsync, isPending: isUpdatingRule } =
    useUpdateProjectGridFieldRule({
      sideEffectQueryKeys: [queryKeys.projectGridFieldRules.all],
      onSuccess: () => {
        showSuccessSnackbar('Field Rule Updated')
      },
      onError: () => {
        showErrorSnackbar('Field Rule Update Failed')
      },
    })

  const { fieldInfos, allFields } = useProjectFieldInfos()

  const addRuleOverlay = useOverlay()

  const [selectedField, setSelectedField] = useState<ProjectGridField | null>(
    null,
  )

  const fieldRulesQuery = useGetProjectGridFieldRules({
    filters: {
      project_grid_field_id: selectedField?.id,
      fields__include: 'rule,workflow_states_ids,dependent_fields_ids',
      limit: '1000',
    },
    enabled: !!selectedField?.id,
  })

  const { projectGridFieldRules, isPending: rulesIsLoading } = fieldRulesQuery

  const defaultRuleId = useMemo(
    () =>
      selectedField
        ? projectGridFieldRules[0]?.id
        : fieldInfos[0].field?.project_grid_field_rules?.[0]?.id,
    [fieldInfos, projectGridFieldRules, selectedField],
  )

  const {
    value: selectedRuleId,
    isInUrl,
    toggleValue,
    resetValue,
  } = useStatefulSearchParam({
    param: 'rule_id',
  })

  const selectedRule = projectGridFieldRules.find(
    (rule) => rule.id === selectedRuleId,
  )

  const setSelectedRuleId = useCallback(
    (ruleId: string | undefined) => {
      if (ruleId) {
        toggleValue(ruleId)
        return
      }

      if (defaultRuleId) {
        toggleValue(defaultRuleId)
      } else if (isInUrl) {
        resetValue()
      }
    },
    [defaultRuleId, isInUrl, resetValue, toggleValue],
  )

  const updateFieldRule = async ({
    workflow_states_ids,
    run_in_all_workflow_states,
    dependent_fields,
    is_superuser_rule,
    ...rest
  }: FormValues) => {
    if (selectedRule) {
      return updateProjectGridFieldRuleAsync({
        ...selectedRule,
        dependent_fields_ids: dependent_fields.map(({ id }) => id),
        workflow_states_ids,
        run_in_all_workflow_states,
        is_superuser_rule,
        params: rest,
      })
    }
    return
  }

  useEffect(() => {
    if (!selectedField) {
      let fieldToSelect = fieldInfos[0].field
      if (isInUrl) {
        const field = allFields.find((f) =>
          f.project_grid_field_rules?.some((r) => r.id === selectedRuleId),
        )
        if (field) {
          fieldToSelect = field
        }
      }
      setSelectedField(fieldToSelect)
    }
  }, [allFields, fieldInfos, isInUrl, selectedField, selectedRuleId])

  useEffect(() => {
    if (selectedField?.id && !rulesIsLoading && !selectedRule) {
      setSelectedRuleId(undefined)
    }
  }, [rulesIsLoading, selectedField?.id, selectedRule, setSelectedRuleId])

  if (!selectedField) return null

  return (
    <>
      <ProjectBreadcrumb label="Field Rules" url="../field-rules" />

      <SettingsPage
        title="Field Rules"
        fill
        description="Rules that check or fill in a field's values. They run in order from top to bottom, so drag a rule to change when it runs."
        width="full"
        actions={
          <Button
            variant="text"
            startIcon={<Add />}
            onClick={addRuleOverlay.open}
            disabled={rulesIsLoading}
          >
            Add rule
          </Button>
        }
      >
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            minHeight: 480,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: 260,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
            }}
          >
            <FieldRulesFieldList
              fieldInfos={fieldInfos}
              selectedField={selectedField}
              setSelectedField={setSelectedField}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              component="h2"
              noWrap
              sx={{
                px: 2.5,
                pt: 2,
                pb: 1,
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {selectedField.full_name || selectedField.name}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pb: 2 }}>
              {/* <RuleTesterSection /> */}
              <FieldRules
                field={selectedField}
                fieldRulesQuery={fieldRulesQuery}
                selectedRuleId={selectedRuleId}
                setSelectedRuleId={setSelectedRuleId}
                addRuleOverlay={addRuleOverlay}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: 1,
              borderColor: 'divider',
            }}
          >
            {selectedRule && (
              <RuleSettings
                key={selectedRule.id} // Completely rerender each time we change the selected rule
                fieldId={selectedField.id}
                isLoading={isUpdatingRule}
                onSubmit={updateFieldRule}
                rule={selectedRule}
                fields={allFields}
              />
            )}
          </Box>
        </Paper>
      </SettingsPage>
    </>
  )
}
